/**
 * Ableitung der Quote-Währung aus einem Asset-Symbol.
 *
 * Krypto- und FX-Symbole tragen die Quote-Währung als Suffix. Wir versuchen
 * bekannte Suffixe zu matchen — sonst geben wir `null` zurück, damit die
 * UI auf einen Fallback (User-Basiswährung) zurückgreifen kann.
 *
 * Beispiele:
 *   BTC-USDT  -> USDT
 *   ETH/EUR   -> EUR
 *   BTCUSDT   -> USDT
 *   XBTEUR    -> EUR
 *   AAPL      -> null  (Aktien tragen die Währung nicht im Symbol)
 */
const QUOTE_SUFFIXES = [
  "USDT", "USDC", "TUSD", "FDUSD", "BUSD", "DAI",
  "USD", "EUR", "GBP", "JPY", "CHF", "CAD", "AUD",
  "BTC", "ETH", "BNB", "SOL",
];

export function deriveQuoteCurrency(asset) {
  if (!asset || typeof asset !== "string") return null;
  const rawSym = asset.trim().toUpperCase();
  if (!rawSym) return null;
  // Kontrakt-Typ-Suffixe (Bitget/Bitunix): `BTCUSDT_UMCBL` → wir nehmen nur
  // den Teil vor dem ersten `_`.
  const sym = rawSym.includes("_") ? rawSym.split("_", 1)[0] : rawSym;
  // Trenner-Fälle: BTC-USDT, ETH/EUR, BTC:USDT. `_` wird NICHT als Trennzeichen
  // verwendet, weil Broker wie Bitget mit Kontrakt-Suffix arbeiten
  // (`BTCUSDT_UMCBL` → Quote soll `USDT` sein, nicht `UMCBL`).
  const parts = sym.split(/[-/:]/);
  if (parts.length > 1) {
    const last = parts[parts.length - 1];
    if (last) return normalize(last);
  }
  // Ohne Trenner: längste Suffix-Übereinstimmung — Rest muss aber ≥ 3 Zeichen
  // sein, sonst matcht z. B. "XBTUSD" fälschlich TUSD (Rest wäre nur "XB").
  const match = QUOTE_SUFFIXES
    .filter((q) => sym.endsWith(q) && sym.length - q.length >= 3)
    .sort((a, b) => b.length - a.length)[0];
  return match ? normalize(match) : null;
}

function normalize(code) {
  // XBT ist Kraken-Sprech für BTC.
  if (code === "XBT") return "BTC";
  return code;
}

/**
 * Formatiert einen Geldbetrag in der Trade-Währung.
 * Für ISO-4217-Währungen (EUR/USD/GBP…) nutzen wir Intl mit „currency"-Style,
 * für Krypto-Symbole (USDT/USDC/BTC…) hängen wir das Ticker-Kürzel an.
 */
const ISO_4217 = new Set([
  "EUR", "USD", "GBP", "JPY", "CHF", "CAD", "AUD",
  "NZD", "SEK", "NOK", "DKK", "PLN", "CZK", "HUF",
]);

export function fmtMoney(value, currency, opts = {}) {
  const { signed = false, digits = 2 } = opts;
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const ccy = currency ? String(currency).trim().toUpperCase() : "";
  const abs = Math.abs(Number(value));
  const sign = value > 0 ? "+" : value < 0 ? "−" : "";
  const prefix = signed ? sign : (value < 0 ? "−" : "");
  if (ccy && ISO_4217.has(ccy)) {
    try {
      const formatted = abs.toLocaleString("de-DE", {
        style: "currency",
        currency: ccy,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
      return prefix ? `${prefix}\u00A0${formatted}` : formatted;
    } catch { /* Fallback unten */ }
  }
  const cryptoDigits = (ccy === "BTC" || ccy === "ETH") ? 6 : digits;
  const num = abs.toLocaleString("de-DE", {
    minimumFractionDigits: cryptoDigits,
    maximumFractionDigits: cryptoDigits,
  });
  // Ohne bekannten Quote-Ticker geben wir nur die reine Zahl zurück —
  // niemals stille Fallback-Währung.
  return ccy ? `${prefix}${num}\u00A0${ccy}` : `${prefix}${num}`;
}
